/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Flag } from "@appchoose/flag";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocalStorageValue } from "@react-hookz/web";
import { PopupButton } from "@typeform/embed-react";
import { atom, useAtom } from "jotai";
import { useAtomValue } from "jotai";
import { Dialog as DialogPrimitive } from "radix-ui";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { ContactUsDialog } from "~/components/contact-us-dialog";
import { ProtectedRouteWithScopes } from "~/components/protected-route-with-scopes/protected-route-with-scopes";
import { SalesSelector } from "~/components/sales-selector/sales-selector";
import { cn } from "~/components/ui/cn";
import { Logo } from "~/components/ui/logo";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useOrdersCountersQuery } from "~/graphql/graphql-new.gen";
import { SaleStatus, StoreRegion, WidgetType } from "~/graphql/graphql.gen";
import { useBrandsAccess } from "~/hooks/use-brands-access";
import { isScopeAuthorized } from "~/libs/auth";
import type { BrandMatch } from "~/routes/root";
import { brandState, serverTimeState } from "~/stores/brand";
import {
  activeSaleState,
  activeSaleWidgetsState,
  isFuture,
  shortActiveSaleIdState,
} from "~/stores/sales";
import { Analytics } from "~/views/analytics/analytics";
import { Billing } from "~/views/billing/billing";
import { Faq } from "~/views/faq/faq";
import { Home } from "~/views/home/home";
import { Orders } from "~/views/orders/orders";
import { Settings } from "~/views/settings/settings";
import { Stock } from "~/views/stock/stock";
import { UpdatingInfo } from "~/views/updating-info/updating-info";

import { MobileSidebar } from "./mobile-sidebar";

export const isSidebarMenuOpenState = atom<boolean>(false);

export function Dashboard() {
  const { user } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const { brandId = "" } = useParams<BrandMatch>();

  const { t } = useTranslation();

  const [isSidebarMenuOpen, setIsSidebarMenuOpen] = useAtom(
    isSidebarMenuOpenState,
  );

  const brand = useAtomValue(brandState);
  const serverTime = useAtomValue(serverTimeState);
  const activeSale = useAtomValue(activeSaleState);
  const shortActiveSaleId = useAtomValue(shortActiveSaleIdState);
  const isInternalUser = user?.["https://appchoose.io/claims/isInternalUser"];

  const { data: brandAccessData } = useBrandsAccess();
  const { data: ordersCountersData } = useOrdersCountersQuery(
    {
      sellerId: brandId,
      saleId: activeSale?.id ?? "",
    },
    {
      enabled: !!brandId && !!activeSale?.id,
      refetchOnWindowFocus: false,
    },
  );

  // /<id>/<path>
  const path = location.pathname.split("/")[2] ?? "";

  useEffect(() => {
    if (isSidebarMenuOpen) setIsSidebarMenuOpen(false);

    if (isInternalUser) {
      if (path === "onboarding") {
        navigate(`/${brandId}/home`, { replace: true });
      }
    } else {
      if (brand?.accept_contract) {
        if (!brand.need_refresh_info) {
          if (path === "updating-info") {
            navigate(`/${brandId}/home`, { replace: true });
          }
        } else {
          if (path !== "updating-info") {
            navigate(`/${brandId}/updating-info/0`, { replace: true });
          }
        }

        if (path === "onboarding") {
          navigate(`/${brandId}/home`, { replace: true });
        }
      } else {
        if (path !== "onboarding") {
          if (user) {
            if (user["https://appchoose.io/claims/needOnboarding"] === true) {
              navigate(`/${brandId}/onboarding/1/2`, { replace: true });
            } else {
              navigate(`/${brandId}/onboarding/2`, { replace: true });
            }
          } else {
            navigate(`/${brandId}/onboarding/0`, { replace: true });
          }
        }
      }
    }
  }, [location]);

  const activeSaleWidgets = useAtomValue(activeSaleWidgetsState);

  const { value: previewRequiredShown } = useLocalStorageValue(
    `preview-sale-${activeSale?.id ?? ""}`,
    {
      initializeWithValue: false,
    },
  );

  const activeSaleDurationWidgets = activeSaleWidgets?.filter(
    (widget) => widget.type === WidgetType.Duration,
  );

  const widgetRequiredCounter =
    activeSaleDurationWidgets && activeSaleDurationWidgets.length > 0 ? 1 : 0;

  const previewRequiredCounter =
    activeSale &&
    isFuture(activeSale, serverTime) &&
    !previewRequiredShown &&
    activeSale.status === SaleStatus.Ready
      ? 1
      : 0;

  const actionRequiredCounter =
    ordersCountersData?.ordersCounters.tagging.brand.actionRequired;
  const hasDifferentDashboardStores =
    new Set(brandAccessData?.map((brand) => brand.store)).size > 1;

  return (
    <>
      <Routes>
        <Route path="updating-info/:step?/*" element={<UpdatingInfo />} />
        <Route
          path="*"
          element={
            <div className="flex h-screen overflow-hidden">
              <section
                className={cn(
                  "hidden min-h-0 w-[192px] flex-col bg-gray-100 py-6 min-[960px]:flex",
                  {
                    "max-[959px]:fixed max-[959px]:inset-0 max-[959px]:z-[40] max-[959px]:flex max-[959px]:size-full max-[959px]:pb-0 max-[959px]:pt-4":
                      isSidebarMenuOpen,
                  },
                )}
              >
                <header className="relative mb-8 flex flex-col items-start min-[960px]:mb-20">
                  <div className="mb-4 mt-14 space-y-4 px-6 min-[960px]:mt-0">
                    <NavLink
                      to={`/${brandId}/home?sid=${shortActiveSaleId}`}
                      className="hidden rounded-lg focus-visible:ring-2 focus-visible:ring-gray-900/30 min-[960px]:inline-block"
                    >
                      {import.meta.env.REACT_APP_STAGE === "production" ? (
                        <Logo role="img" aria-label="Logo Choose" />
                      ) : (
                        <Logo
                          appearance="white"
                          role="img"
                          aria-label="Logo Choose"
                        />
                      )}
                    </NavLink>

                    <p className="hidden items-center space-x-2 font-semibold leading-5.5 text-gray-900 min-[960px]:block">
                      <span className="inline">{brand?.name}</span>

                      {hasDifferentDashboardStores ? (
                        <Tooltip>
                          <TooltipTrigger>
                            <Flag
                              flag={
                                brand?.store === StoreRegion.Us ? "US" : "EU"
                              }
                              viewBox="0 0 24 24"
                              className="inline h-[22px] w-[18px]"
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="px-1.5 py-3">
                              {t("sidebar.store", {
                                store:
                                  brand?.store === StoreRegion.Us
                                    ? t("region.united_states")
                                    : t("region.europe"),
                              })}
                            </div>
                            <TooltipArrow />
                          </TooltipContent>
                        </Tooltip>
                      ) : null}
                    </p>
                  </div>
                  <NavLink
                    to={`/${brandId}/settings?sid=${shortActiveSaleId}`}
                    className="border-gray-500 px-4 text-xs hover:font-bold aria-[current=page]:border-l-2 aria-[current=page]:px-3.5 aria-[current=page]:font-bold aria-[current=page]:text-gray-900 min-[960px]:px-6 min-[960px]:aria-[current=page]:px-5.5"
                  >
                    {t("sidebar.settings")}
                  </NavLink>
                </header>
                <div className="flex flex-1 flex-col overflow-y-auto">
                  <div className="flex-1 space-y-8 min-[960px]:space-y-4">
                    <NavLink
                      to={`/${brandId}/home?sid=${shortActiveSaleId}`}
                      className="flex flex-row items-center space-x-3 pl-4 pr-6 text-2xl text-gray-700 hover:font-bold hover:text-gray-900 aria-[current=page]:border-l-2 aria-[current=page]:border-gray-500 aria-[current=page]:pl-3.5 aria-[current=page]:font-bold aria-[current=page]:text-gray-900 min-[960px]:justify-between min-[960px]:pl-6 min-[960px]:text-base min-[960px]:aria-[current=page]:pl-5.5"
                    >
                      <span>{t("sidebar.home")}</span>
                      {widgetRequiredCounter || previewRequiredCounter ? (
                        <p className="block rounded bg-green-900 px-1.25 py-1 text-xs font-semibold text-white">
                          {widgetRequiredCounter + previewRequiredCounter}
                        </p>
                      ) : null}
                    </NavLink>
                    <NavLink
                      to={`/${brandId}/orders?sid=${shortActiveSaleId}`}
                      className="flex flex-row items-center space-x-3 pl-4 pr-6 text-2xl text-gray-700 hover:font-bold hover:text-gray-900 aria-[current=page]:border-l-2 aria-[current=page]:border-gray-500 aria-[current=page]:pl-3.5 aria-[current=page]:font-bold aria-[current=page]:text-gray-900 min-[960px]:justify-between min-[960px]:pl-6 min-[960px]:text-base min-[960px]:aria-[current=page]:pl-5.5"
                    >
                      <span>{t("sidebar.orders")}</span>
                      {actionRequiredCounter && actionRequiredCounter > 0 ? (
                        <p className="block rounded bg-green-900 px-1.25 py-1 text-xs font-semibold text-white">
                          {actionRequiredCounter}
                        </p>
                      ) : null}
                    </NavLink>
                    <NavLink
                      to={`/${brandId}/stock?sid=${shortActiveSaleId}`}
                      className="flex flex-row items-center pl-4 pr-6 text-2xl text-gray-700 hover:font-bold hover:text-gray-900 aria-[current=page]:border-l-2 aria-[current=page]:border-gray-500 aria-[current=page]:pl-3.5 aria-[current=page]:font-bold aria-[current=page]:text-gray-900 min-[960px]:pl-6 min-[960px]:text-base min-[960px]:aria-[current=page]:pl-5.5"
                    >
                      {t("sidebar.stock")}
                    </NavLink>
                    {isScopeAuthorized(user, "scope.brand.analytics.view") ? (
                      <NavLink
                        to={`/${brandId}/analytics?sid=${shortActiveSaleId}`}
                        className="flex flex-row items-center pl-4 pr-6 text-2xl text-gray-700 hover:font-bold hover:text-gray-900 aria-[current=page]:border-l-2 aria-[current=page]:border-gray-500 aria-[current=page]:pl-3.5 aria-[current=page]:font-bold aria-[current=page]:text-gray-900 min-[960px]:pl-6 min-[960px]:text-base min-[960px]:aria-[current=page]:pl-5.5"
                      >
                        {t("sidebar.analytics")}
                      </NavLink>
                    ) : null}
                    {isScopeAuthorized(user, "scope.brand.billing.view") ? (
                      <NavLink
                        to={`/${brandId}/billing?sid=${shortActiveSaleId}`}
                        className="flex flex-row items-center pl-4 pr-6 text-2xl text-gray-700 hover:font-bold hover:text-gray-900 aria-[current=page]:border-l-2 aria-[current=page]:border-gray-500 aria-[current=page]:pl-3.5 aria-[current=page]:font-bold aria-[current=page]:text-gray-900 min-[960px]:pl-6 min-[960px]:text-base min-[960px]:aria-[current=page]:pl-5.5"
                      >
                        {t("sidebar.billing")}
                      </NavLink>
                    ) : null}
                  </div>
                  <div className="mb-4 flex flex-col space-y-4">
                    <NavLink
                      to={`/${brandId}/faq?sid=${shortActiveSaleId}`}
                      className="flex flex-row items-center pl-4 pr-6 text-sm text-gray-700 hover:font-bold hover:text-gray-900 aria-[current=page]:border-l-2 aria-[current=page]:border-gray-500 aria-[current=page]:pl-3.5 aria-[current=page]:font-bold aria-[current=page]:text-gray-900 min-[960px]:pl-6 min-[960px]:aria-[current=page]:pl-5.5"
                    >
                      {t("sidebar.faq")}
                    </NavLink>
                    <DialogPrimitive.Root modal={false}>
                      <DialogPrimitive.Trigger asChild>
                        <button
                          type="button"
                          className="flex flex-row items-center pl-4 pr-6 text-sm text-gray-700 hover:font-bold hover:text-gray-900 aria-[current=page]:border-l-2 aria-[current=page]:border-gray-500 aria-[current=page]:pl-3.5 aria-[current=page]:font-bold min-[960px]:pl-6 min-[960px]:aria-[current=page]:pl-5.5"
                        >
                          {t("sidebar.contact_us")}
                        </button>
                      </DialogPrimitive.Trigger>
                      <ContactUsDialog />
                    </DialogPrimitive.Root>
                  </div>
                </div>
                <div className="mx-6 border-t border-gray-300 pt-4">
                  <SalesSelector />
                </div>
              </section>
              <div className="flex w-0 flex-1 flex-col overflow-hidden">
                <MobileSidebar />
                <Routes>
                  <Route path="orders/:orderId?/*" element={<Orders />} />
                  <Route path="home/*" element={<Home />} />
                  <Route path="settings/*" element={<Settings />} />
                  <Route
                    path="billing/*"
                    element={
                      <ProtectedRouteWithScopes scope="scope.brand.billing.view">
                        <Billing />
                      </ProtectedRouteWithScopes>
                    }
                  />
                  <Route path="stock/*" element={<Stock />} />
                  <Route
                    path="analytics"
                    element={
                      <ProtectedRouteWithScopes scope="scope.brand.analytics.view">
                        <Analytics />
                      </ProtectedRouteWithScopes>
                    }
                  />
                  <Route path="faq/*" element={<Faq />} />
                  <Route
                    path="*"
                    element={<Navigate to={`/${brandId}/home`} />}
                  />
                </Routes>
              </div>
            </div>
          }
        />
      </Routes>
      <PopupButton
        hidden={{
          brand_id: brand?.id ?? "",
          email: user?.email ?? "",
          first_name:
            user?.["https://appchoose.io/claims/firstname"] ?? user?.given_name,
        }}
        id="Su0f21ho"
        open="load"
        preventReopenOnClose
        autoClose={true}
        size={90}
      />
    </>
  );
}
